import { AICommand } from '@/utils/aiCommandUtils'

// Intent analysis structure
export interface IntentAnalysis {
  primaryIntent: {
    category: string
    description: string
    confidence: number // 0-100
  }
  secondaryIntents: {
    category: string
    description: string
    confidence: number // 0-100
  }[]
  domain: string
  complexity: 'low' | 'medium' | 'high'
  urgency: 'low' | 'medium' | 'high'
  completeness: number // 0-100, how complete is the provided information
}

// Have and Need analysis structure
export interface HaveAndNeedAnalysis {
  have: {
    strengths: string[]
    availableInformation: string[]
    existingAssets: string[]
    clarity: number // 0-100
  }
  need: {
    missingInformation: string[]
    requiredResources: string[]
    knowledgeGaps: string[]
    actionItems: string[]
    priority: 'low' | 'medium' | 'high'
  }
  gap: {
    description: string
    criticalMissing: string[]
    recommendedNextSteps: string[]
  }
}

// DIKW Model interfaces
export interface DataExtraction {
  rawFacts: string[]
  numbers: string[]
  dates: string[]
  names: string[]
  locations: string[]
  quantities: string[]
  categories: string[]
  sources: string[]
  metadata: {
    extractionConfidence: number // 0-100
    totalDataPoints: number
    dataQuality: 'high' | 'medium' | 'low'
  }
}

export interface InformationExtraction {
  processedData: string[]
  contexts: string[]
  relationships: string[]
  patterns: string[]
  trends: string[]
  summaries: string[]
  classifications: string[]
  interpretations: string[]
  metadata: {
    contextualRelevance: number // 0-100
    informationDensity: 'high' | 'medium' | 'low'
    clarity: number // 0-100
  }
}

export interface KnowledgeExtraction {
  insights: string[]
  principles: string[]
  bestPractices: string[]
  lessons: string[]
  frameworks: string[]
  methodologies: string[]
  experiences: string[]
  expertise: string[]
  metadata: {
    knowledgeDepth: number // 0-100
    applicability: 'universal' | 'domain-specific' | 'context-specific'
    evidenceStrength: 'strong' | 'moderate' | 'weak'
  }
}

export interface WisdomExtraction {
  judgments: string[]
  recommendations: string[]
  strategicInsights: string[]
  ethicalConsiderations: string[]
  longTermImplications: string[]
  balancedPerspectives: string[]
  cautionaryAdvice: string[]
  paradoxes: string[]
  metadata: {
    wisdomLevel: number // 0-100
    practicalValue: 'high' | 'medium' | 'low'
    universalApplicability: number // 0-100
  }
}

export interface SmartNotesNodeData {
  content?: string
  createdBy: string,
  recommendations?: string[]
  statusMessage?: string
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  assignedEmails?: string[] | string
  documentRequest?: AICommand & {
    document: string
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
  linkedKnowledge?: string
  extractLinks?: boolean
  extractAdultContent?: boolean
  extractDepthLevel?: number
  setReferences?: Record<string, string[]>
  context?: string
  linkedNodes?: string[]
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  dataSettings?: SmartNotesDataSettings
  lastModified?: number
  minifiedInfo?: Record<string, any>
  configuration?: SmartNotesConfiguration
  // Analysis fields
  intentAnalysis?: IntentAnalysis
  haveAndNeedAnalysis?: HaveAndNeedAnalysis
  // DIKW model extractions
  dataExtraction?: DataExtraction
  informationExtraction?: InformationExtraction
  knowledgeExtraction?: KnowledgeExtraction
  wisdomExtraction?: WisdomExtraction
}

export interface SmartNotesDataSettings {
  inputLinks: any[]
  outputLinks: any[]
  availableNodes: any[]
  exposedData?: any
  autoSync?: boolean
  [key: string]: any
}

export interface SmartNotesConfiguration {
  extractLinksDefault: boolean
  extractAdultContent: boolean
  extractDepthLevel: number
  autoAnalyze: boolean
  [key: string]: any
}

export interface SmartNotesExposedData {
  content: {
    value: string
    enabled: boolean
    label: string
    description: string
    permissions: { readable: boolean; writable: boolean }
  }
  recommendations: {
    value: string[]
    enabled: boolean
    label: string
    description: string
    permissions: { readable: boolean; writable: boolean }
  }
  linkedKnowledge: {
    value: string
    enabled: boolean
    label: string
    description: string
    permissions: { readable: boolean; writable: boolean }
  }
}

export interface SmartNotesRecommendation {
  title: string
  documentKey?: string
  action: 'use-existing' | 'create-new'
  description: string
  why: string
  purpose: string
  score: number // 0-100
  group: 'refinable-content' | 'template-recommendations' // Added for grouping
  requiredInputs?: string[] // For templates - what inputs are needed
  missingInformation?: string[] // For templates - what information is missing
  guidance?: string // For templates - specific guidance on how to complete
  extra?: Record<string, any>
}

// Utility function to handle scrolling (copied from DocumentTabs)
export function handleScroll(element: HTMLElement, deltaY: number): boolean {
  if (!element) return false
  const { scrollTop, scrollHeight, clientHeight } = element
  const isAtTop = scrollTop <= 0
  const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2
  const isScrollingUp = deltaY < 0
  const isScrollingDown = deltaY > 0

  if ((isScrollingUp && !isAtTop) || (isScrollingDown && !isAtBottom)) {
    element.scrollTop += deltaY
    return true // Event was handled
  }
  return false // Let it bubble for panning
}

// Helper to extract only the markdown code block from generatedMarkdown (without code block markers)
export function extractMarkdownSection(markdown: string): string {
  if (!markdown) return ''
  // Find all code blocks and pick the first with markdown or md
  const codeBlocks = Array.from(
    markdown.matchAll(/```(\w+)[^\n]*\n([\s\S]*?)```/g)
  )
  for (const match of codeBlocks) {
    const lang = match[1].toLowerCase()
    if (lang === 'markdown' || lang === 'md') {
      return match[2].trim()
    }
  }
  // fallback: return first code block content if exists
  if (codeBlocks.length > 0) {
    return codeBlocks[0][2].trim()
  }
  return ''
}

export const PROCESSING_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes - increased for comprehensive SmartNotes processing (DIKW + recommendations)
